(() => {

  /*
   * ==========================================
   * PREVENT DUPLICATE SCRIPT
   * ==========================================
   */

  if (window.__nirmalDeliveryPreferencesLoaded) {
    return;
  }

  window.__nirmalDeliveryPreferencesLoaded = true;


  /*
   * ==========================================
   * CONSTANTS
   * ==========================================
   */

  const BOX_SELECTOR =
    '[data-nirmal-delivery-preferences]';

  const nativeFetch =
    window.fetch.bind(window);


  let busy = false;

  let mutationSyncPending = false;

  let mutationTimer = null;


  /*
   * ==========================================
   * SHOPIFY ROOT
   * ==========================================
   */

  function root() {

    return (
      window.Shopify?.routes?.root ||
      '/'
    );

  }


  /*
   * ==========================================
   * GET ALL DELIVERY BOXES
   * ==========================================
   */

  function getBoxes() {

    return Array.from(
      document.querySelectorAll(
        BOX_SELECTOR
      )
    );

  }


  /*
   * ==========================================
   * GET BOX CONTEXT
   * ==========================================
   */

  function getContext(box) {

    return (
      box?.dataset?.nirmalContext ||
      'cart'
    );

  }


  /*
   * ==========================================
   * GET COURIER
   * ==========================================
   */

  function getCourier(box) {

    return (
      box
        ?.querySelector(
          '[data-nirmal-courier]'
        )
        ?.value ||
      ''
    );

  }


  /*
   * ==========================================
   * GET DELIVERY TYPE
   * ==========================================
   */

  function getDeliveryType(box) {

    return (
      box
        ?.querySelector(
          '[data-nirmal-delivery-type]:checked'
        )
        ?.value ||
      ''
    );

  }


  /*
   * ==========================================
   * FIND EXPRESS FEE VARIANT
   * ==========================================
   */

  function getFeeVariantId(box = null) {

    const directId =
      Number(
        box?.dataset?.feeVariantId ||
        0
      );


    if (directId) {
      return directId;
    }


    for (const currentBox of getBoxes()) {

      const id =
        Number(
          currentBox.dataset.feeVariantId ||
          0
        );


      if (id) {
        return id;
      }

    }


    return 0;

  }


  /*
   * ==========================================
   * GET FEE PRICE
   * ==========================================
   */

  function getFeePriceCents(box) {

    return Number(
      box?.dataset?.feePriceCents ||
      0
    );

  }


  /*
   * ==========================================
   * MONEY FORMAT
   * ==========================================
   */

  function formatMoney(cents) {

    const amount =
      Number(cents || 0) / 100;


    const currency =
      window.Shopify
        ?.currency
        ?.active ||
      'INR';


    if (currency === 'INR') {

      return (
        'Rs. ' +
        amount.toFixed(2)
      );

    }


    try {

      return new Intl.NumberFormat(
        undefined,
        {
          style: 'currency',
          currency
        }
      ).format(amount);

    }

    catch (error) {

      return amount.toFixed(2);

    }

  }


  /*
   * ==========================================
   * CART GET
   * ==========================================
   */

  async function getCart() {

    const response =
      await nativeFetch(
        root() + 'cart.js',
        {
          credentials: 'same-origin',

          headers: {
            Accept:
              'application/json'
          }
        }
      );


    if (!response.ok) {

      throw new Error(
        'Unable to load cart.'
      );

    }


    return response.json();

  }


  /*
   * ==========================================
   * CART POST
   * ==========================================
   */

  async function postCart(
    endpoint,
    body
  ) {

    const response =
      await nativeFetch(
        root() + endpoint,
        {
          method: 'POST',

          credentials:
            'same-origin',

          headers: {

            'Content-Type':
              'application/json',

            Accept:
              'application/json'

          },

          body:
            JSON.stringify(body)

        }
      );


    if (!response.ok) {

      let message =
        'Unable to update cart.';


      try {

        const error =
          await response.json();


        message =
          error.description ||
          error.message ||
          message;

      }

      catch (error) {}


      throw new Error(message);

    }


    return response.json();

  }


  /*
   * ==========================================
   * STATUS
   * ==========================================
   */

  function setStatus(
    box,
    message
  ) {

    const status =
      box?.querySelector(
        '[data-nirmal-delivery-status]'
      );


    if (status) {

      status.textContent =
        message || '';

    }

  }


  /*
   * ==========================================
   * ERROR
   * ==========================================
   */

  function showError(
    box,
    message
  ) {

    const error =
      box?.querySelector(
        '[data-nirmal-delivery-error]'
      );


    if (!error) {
      return;
    }


    error.textContent =
      message;


    error.classList.add(
      'is-visible'
    );

  }


  function clearError(box) {

    const error =
      box?.querySelector(
        '[data-nirmal-delivery-error]'
      );


    if (!error) {
      return;
    }


    error.textContent = '';


    error.classList.remove(
      'is-visible'
    );

  }


  /*
   * ==========================================
   * LOADING
   * ==========================================
   */

  function setLoading(
    box,
    loading
  ) {

    if (!box) {
      return;
    }


    if (loading) {

      box.setAttribute(
        'data-loading',
        'true'
      );

    }

    else {

      box.removeAttribute(
        'data-loading'
      );

    }

  }


  /*
   * ==========================================
   * SYNC ALL UI BOXES
   * ==========================================
   */

  function syncSelections(
    sourceBox
  ) {

    const courier =
      getCourier(sourceBox);


    const deliveryType =
      getDeliveryType(sourceBox);


    getBoxes().forEach(box => {

      if (box === sourceBox) {
        return;
      }


      const courierSelect =
        box.querySelector(
          '[data-nirmal-courier]'
        );


      if (courierSelect) {

        const exists =
          Array.from(
            courierSelect.options
          ).some(
            option =>
              option.value ===
              courier
          );


        if (exists) {

          courierSelect.value =
            courier;

        }

      }


      box
        .querySelectorAll(
          '[data-nirmal-delivery-type]'
        )
        .forEach(radio => {

          radio.checked =
            radio.value ===
            deliveryType;

        });

    });


    updateProductCalculations();

  }


  /*
   * ==========================================
   * SAVE CART ATTRIBUTES
   * ==========================================
   */

  async function savePreferences(box) {

    const courier =
      getCourier(box);


    const deliveryType =
      getDeliveryType(box);


    await postCart(
      'cart/update.js',
      {
        attributes: {

          'Courier Partner':
            courier,

          'Delivery Type':
            deliveryType,

          'Express Delivery':
            deliveryType ===
            'Express'
              ? 'Yes'
              : 'No',

          'Express Delivery Charge':
            deliveryType ===
            'Express'
              ? '₹69 per item'
              : 'No'

        }
      }
    );

  }


  /*
   * ==========================================
   * COUNT REAL PRODUCTS
   *
   * EXPRESS FEE PRODUCT IS NOT INCLUDED.
   * ==========================================
   */

  function getCartQuantities(
    cart,
    feeVariantId
  ) {

    let realQuantity = 0;

    let feeItem = null;


    cart.items.forEach(item => {

      if (
        Number(item.variant_id) ===
        Number(feeVariantId)
      ) {

        feeItem = item;

      }

      else {

        realQuantity +=
          Number(item.quantity || 0);

      }

    });


    return {
      realQuantity,
      feeItem
    };

  }


  /*
   * ==========================================
   * ADD / UPDATE / REMOVE EXPRESS FEE
   * ==========================================
   */

  async function ensureExpressFee({
    box = null,
    cart = null,
    deliveryType = null,
    showStatus = true
  } = {}) {

    const feeVariantId =
      getFeeVariantId(box);


    if (!cart) {

      cart =
        await getCart();

    }


    if (!deliveryType) {

      deliveryType =
        getDeliveryType(box) ||
        cart.attributes?.[
          'Delivery Type'
        ] ||
        '';

    }


    /*
     * If Express was selected but the
     * fee product does not exist.
     */

    if (
      deliveryType === 'Express' &&
      !feeVariantId
    ) {

      throw new Error(
        'Express Delivery Fee product variant was not found.'
      );

    }


    /*
     * If fee product does not exist and
     * Express is not selected, nothing to do.
     */

    if (!feeVariantId) {

      return false;

    }


    const {
      realQuantity,
      feeItem
    } =
      getCartQuantities(
        cart,
        feeVariantId
      );


    const desiredFeeQuantity =
      deliveryType === 'Express'
        ? realQuantity
        : 0;


    /*
     * ===============================
     * ADD EXPRESS FEE
     * ===============================
     */

    if (
      desiredFeeQuantity > 0 &&
      !feeItem
    ) {

      if (
        showStatus &&
        box
      ) {

        setStatus(
          box,
          'Adding Express Delivery charge...'
        );

      }


      await postCart(
        'cart/add.js',
        {
          items: [
            {
              id:
                feeVariantId,

              quantity:
                desiredFeeQuantity,

              properties: {
                '_delivery_fee':
                  'Express Delivery'
              }
            }
          ]
        }
      );


      return true;

    }


    /*
     * ===============================
     * UPDATE EXPRESS FEE QUANTITY
     * ===============================
     */

    if (
      feeItem &&
      Number(feeItem.quantity) !==
      desiredFeeQuantity
    ) {

      if (
        showStatus &&
        box
      ) {

        if (
          desiredFeeQuantity > 0
        ) {

          setStatus(
            box,
            'Updating Express Delivery charge...'
          );

        }

        else {

          setStatus(
            box,
            'Removing Express Delivery charge...'
          );

        }

      }


      await postCart(
        'cart/change.js',
        {
          id:
            feeItem.key,

          quantity:
            desiredFeeQuantity
        }
      );


      return true;

    }


    return false;

  }


  /*
   * ==========================================
   * PRODUCT QUANTITY
   * ==========================================
   */

  function getProductQuantity(box) {

    let scope =
      box.closest(
        'product-info'
      );


    if (!scope) {

      scope =
        box.closest(
          '.product'
        );

    }


    if (!scope) {

      scope =
        box.closest(
          'section'
        );

    }


    let quantityInput =
      scope?.querySelector(
        'input[name="quantity"]'
      );


    if (!quantityInput) {

      quantityInput =
        document.querySelector(
          'input[name="quantity"]'
        );

    }


    const quantity =
      parseInt(
        quantityInput?.value ||
        '1',
        10
      );


    return (
      Number.isFinite(quantity) &&
      quantity > 0
    )
      ? quantity
      : 1;

  }


  /*
   * ==========================================
   * PDP EXPRESS CALCULATION
   * ==========================================
   */

  function updateProductBoxCalculation(
    box
  ) {

    if (
      getContext(box) !==
      'product'
    ) {
      return;
    }


    const quantity =
      getProductQuantity(box);


    const price =
      getFeePriceCents(box);


    const qtyElement =
      box.querySelector(
        '[data-nirmal-express-qty]'
      );


    const totalElement =
      box.querySelector(
        '[data-nirmal-express-total]'
      );


    if (qtyElement) {

      qtyElement.textContent =
        quantity;

    }


    if (totalElement) {

      totalElement.textContent =
        formatMoney(
          price * quantity
        );

    }

  }


  function updateProductCalculations() {

    getBoxes()
      .filter(
        box =>
          getContext(box) ===
          'product'
      )
      .forEach(
        updateProductBoxCalculation
      );

  }


  /*
   * ==========================================
   * CART EXPRESS CALCULATION
   * ==========================================
   */

  function updateCartCalculations(
    cart
  ) {

    const feeVariantId =
      getFeeVariantId();


    if (!feeVariantId) {
      return;
    }


    const {
      realQuantity
    } =
      getCartQuantities(
        cart,
        feeVariantId
      );


    getBoxes()
      .filter(
        box =>
          getContext(box) ===
          'cart'
      )
      .forEach(box => {

        const price =
          getFeePriceCents(box);


        const qtyElement =
          box.querySelector(
            '[data-nirmal-express-qty]'
          );


        const totalElement =
          box.querySelector(
            '[data-nirmal-express-total]'
          );


        if (qtyElement) {

          qtyElement.textContent =
            realQuantity;

        }


        if (totalElement) {

          totalElement.textContent =
            formatMoney(
              price *
              realQuantity
            );

        }

      });

  }


  function updateAllCalculations(
    cart = null
  ) {

    updateProductCalculations();


    if (cart) {

      updateCartCalculations(
        cart
      );

    }

  }


  /*
   * ==========================================
   * HANDLE CUSTOMER CHANGING DELIVERY
   * ==========================================
   */

  async function handleDeliveryChange(
    box
  ) {

    if (
      !box ||
      busy
    ) {
      return;
    }


    busy = true;


    setLoading(
      box,
      true
    );


    clearError(box);


    try {

      /*
       * Keep PDP and Cart selections identical.
       */

      syncSelections(box);


      /*
       * Save values to Shopify cart attributes.
       */

      await savePreferences(box);


      const cart =
        await getCart();


      /*
       * Add/update/remove Express fee.
       *
       * On PDP the current product may not yet
       * be in cart. That's okay. The script
       * synchronizes again after Add to Cart.
       */

      const changed =
        await ensureExpressFee({
          box,
          cart,
          deliveryType:
            getDeliveryType(box),
          showStatus:
            true
        });


      /*
       * On CART:
       * reload so Shopify totals visibly update.
       */

      if (
        changed &&
        getContext(box) ===
        'cart'
      ) {

        reloadCartAfterFeeChange();

        return;

      }


      const finalCart =
        changed
          ? await getCart()
          : cart;


      updateAllCalculations(
        finalCart
      );


      setStatus(
        box,
        'Delivery preference saved.'
      );

    }

    catch (error) {

      console.error(
        'Delivery error:',
        error
      );


      showError(
        box,
        error.message ||
        'Unable to update delivery.'
      );

    }

    finally {

      setLoading(
        box,
        false
      );


      busy = false;

    }

  }


  /*
   * ==========================================
   * DELIVERY CHANGE LISTENER
   * ==========================================
   */

/*
 * ==========================================
 * BLUE DART POPUP
 * ==========================================
 */

function openBlueDartPopup() {

  const popup =
    document.getElementById(
      'nirmal-blue-dart-popup'
    );


  if (!popup) {
    return;
  }


  popup.classList.add(
    'is-visible'
  );


  popup.setAttribute(
    'aria-hidden',
    'false'
  );


  document.body.style.overflow =
    'hidden';

}



function closeBlueDartPopup() {

  const popup =
    document.getElementById(
      'nirmal-blue-dart-popup'
    );


  if (!popup) {
    return;
  }


  popup.classList.remove(
    'is-visible'
  );


  popup.setAttribute(
    'aria-hidden',
    'true'
  );


  document.body.style.overflow =
    '';

}


/*
 * POPUP CLOSE
 */

document.addEventListener(
  'click',
  event => {

    if (
      !(
        event.target instanceof
        Element
      )
    ) {
      return;
    }


    if (
      !event.target.closest(
        '[data-blue-dart-close]'
      )
    ) {
      return;
    }


    closeBlueDartPopup();

  }
);


/*
 * CLOSE WITH ESC KEY
 */

document.addEventListener(
  'keydown',
  event => {

    if (
      event.key ===
      'Escape'
    ) {

      closeBlueDartPopup();

    }

  }
);


/*
 * ==========================================
 * DELIVERY CHANGE LISTENER
 * ==========================================
 */

document.addEventListener(
  'change',
  event => {

    if (
      !(
        event.target instanceof
        Element
      )
    ) {
      return;
    }


    if (
      !event.target.matches(
        [
          '[data-nirmal-courier]',
          '[data-nirmal-delivery-type]'
        ].join(',')
      )
    ) {
      return;
    }


    const box =
      event.target.closest(
        BOX_SELECTOR
      );


    if (!box) {
      return;
    }


    /*
     * ======================================
     * BLUE DART
     * ======================================
     */

    if (
      event.target.matches(
        '[data-nirmal-courier]'
      ) &&
      event.target.value ===
        'Blue Dart'
    ) {

      /*
       * Show unavailable popup
       */

      openBlueDartPopup();


      /*
       * Remove Blue Dart selection
       */

      event.target.value = '';


      /*
       * Keep other delivery boxes
       * synchronized.
       */

      getBoxes().forEach(
        currentBox => {

          const courierSelect =
            currentBox.querySelector(
              '[data-nirmal-courier]'
            );


          if (
            courierSelect &&
            courierSelect !==
              event.target
          ) {

            courierSelect.value = '';

          }

        }
      );


      /*
       * Do NOT call
       * handleDeliveryChange().
       *
       * Therefore Blue Dart will never
       * be saved as cart attribute.
       */

      return;

    }


    /*
     * Normal courier / delivery selection
     */

    handleDeliveryChange(
      box
    );

  }
);


  /*
   * ==========================================
   * PDP QUANTITY CHANGES
   * ==========================================
   */

  function quantityChanged(event) {

    if (
      !(
        event.target instanceof
        Element
      )
    ) {
      return;
    }


    if (
      event.target.matches(
        'input[name="quantity"]'
      )
    ) {

      setTimeout(
        updateProductCalculations,
        20
      );

    }

  }


  document.addEventListener(
    'input',
    quantityChanged
  );


  document.addEventListener(
    'change',
    quantityChanged
  );


  /*
   * Quantity +/- buttons.
   */

  document.addEventListener(
    'click',
    event => {

      if (
        !(
          event.target instanceof
          Element
        )
      ) {
        return;
      }


      const button =
        event.target.closest(
          [
            'quantity-input button',
            '.quantity button',
            '[name="plus"]',
            '[name="minus"]'
          ].join(',')
        );


      if (!button) {
        return;
      }


      setTimeout(
        updateProductCalculations,
        50
      );

    }
  );


  /*
   * ==========================================
   * VALIDATE PDP BEFORE ADD TO CART
   * ==========================================
   */

  document.addEventListener(
    'submit',
    event => {

      const form =
        event.target;


      if (
        !(
          form instanceof
          HTMLFormElement
        )
      ) {
        return;
      }


      const action =
        form.getAttribute(
          'action'
        ) || '';


      if (
        !action.includes(
          '/cart/add'
        )
      ) {
        return;
      }


      const scope =
        form.closest(
          'product-info, section, .product'
        );


      const box =
        scope?.querySelector(
          BOX_SELECTOR +
          '[data-nirmal-context="product"]'
        );


      if (!box) {
        return;
      }


      clearError(box);


      const courier =
        getCourier(box);


      const deliveryType =
        getDeliveryType(box);


      if (!courier) {

        event.preventDefault();

        event.stopImmediatePropagation();


        showError(
          box,
          'Please select your preferred courier partner.'
        );


        box.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });


        return;

      }


      if (!deliveryType) {

        event.preventDefault();

        event.stopImmediatePropagation();


        showError(
          box,
          'Please select Standard Delivery or Express Delivery.'
        );


        box.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });


        return;

      }


      if (
        busy ||
        mutationSyncPending
      ) {

        event.preventDefault();

        event.stopImmediatePropagation();


        showError(
          box,
          'Please wait while your delivery preference is being saved.'
        );

      }

    },

    true
  );


  /*
   * ==========================================
   * CHECKOUT VALIDATION
   * ==========================================
   */

  function getCheckoutBox() {

    const cartBoxes =
      getBoxes().filter(
        box =>
          getContext(box) ===
          'cart'
      );


    if (cartBoxes.length) {

      return cartBoxes[0];

    }


    return getBoxes()[0] || null;

  }


  document.addEventListener(
    'click',
    event => {

      if (
        !(
          event.target instanceof
          Element
        )
      ) {
        return;
      }


      const checkout =
        event.target.closest(
          [
            '#checkout',
            '.cart__checkout-button',
            'button[name="checkout"]',
            'a[href*="/checkout"]'
          ].join(',')
        );


      if (!checkout) {
        return;
      }


      const box =
        getCheckoutBox();


      if (!box) {
        return;
      }


      clearError(box);


      const courier =
        getCourier(box);


      const deliveryType =
        getDeliveryType(box);


      if (!courier) {

        event.preventDefault();

        event.stopImmediatePropagation();


        showError(
          box,
          'Please select your preferred courier partner.'
        );


        box.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });


        return false;

      }


      if (!deliveryType) {

        event.preventDefault();

        event.stopImmediatePropagation();


        showError(
          box,
          'Please select Standard Delivery or Express Delivery.'
        );


        box.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });


        return false;

      }


      if (
        busy ||
        mutationSyncPending
      ) {

        event.preventDefault();

        event.stopImmediatePropagation();


        showError(
          box,
          'Please wait while your delivery charge is updating.'
        );


        return false;

      }

    },

    true
  );


  /*
   * ==========================================
   * REFRESH CART
   * ==========================================
   */

  function isCartPage() {

    return (
      /\/cart\/?$/.test(
        window.location.pathname
      )
    );

  }


  function reloadCartAfterFeeChange() {

    if (isCartPage()) {

      window.location.reload();

      return;

    }


    sessionStorage.setItem(
      'nirmal-open-cart',
      '1'
    );


    window.location.reload();

  }


  /*
   * ==========================================
   * OPEN CART AFTER RELOAD
   * ==========================================
   */

  function openCartAfterReload() {

    const openCart =
      sessionStorage.getItem(
        'nirmal-open-cart'
      );


    if (
      openCart !== '1'
    ) {
      return;
    }


    sessionStorage.removeItem(
      'nirmal-open-cart'
    );


    setTimeout(
      () => {

        const drawer =
          document.querySelector(
            'cart-drawer-component'
          );


        if (
          drawer &&
          typeof drawer.open ===
          'function'
        ) {

          drawer.open();

          return;

        }


        document
          .querySelector(
            '[data-testid="cart-drawer-trigger"]'
          )
          ?.click();

      },

      400
    );

  }


  /*
   * ==========================================
   * AUTOMATICALLY SYNC EXPRESS FEE AFTER
   * ADD TO CART / QUANTITY CHANGE
   * ==========================================
   */

  function isCartMutation(
    input
  ) {

    let url = '';


    if (
      typeof input ===
      'string'
    ) {

      url = input;

    }

    else if (
      input &&
      typeof input.url ===
      'string'
    ) {

      url = input.url;

    }


    return (
      /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/i
        .test(url)
    );

  }


  async function syncAfterCartMutation() {

    if (busy) {

      scheduleCartMutationSync();

      return;

    }


    busy = true;


    try {

      const cart =
        await getCart();


      const deliveryType =
        cart.attributes?.[
          'Delivery Type'
        ] || '';


      const changed =
        await ensureExpressFee({
          cart,
          deliveryType,
          showStatus:
            false
        });


      const finalCart =
        changed
          ? await getCart()
          : cart;


      updateAllCalculations(
        finalCart
      );


      /*
       * Full Cart page:
       * refresh visible total after fee qty changes.
       */

      if (
        changed &&
        isCartPage()
      ) {

        window.location.reload();

      }

    }

    catch (error) {

      console.error(
        'Automatic Express Delivery sync error:',
        error
      );

    }

    finally {

      busy = false;

      mutationSyncPending =
        false;

    }

  }


  function scheduleCartMutationSync() {

    mutationSyncPending =
      true;


    clearTimeout(
      mutationTimer
    );


    mutationTimer =
      setTimeout(
        syncAfterCartMutation,
        100
      );

  }


  /*
   * Watch Shopify Ajax cart requests.
   *
   * Our own requests use nativeFetch directly,
   * therefore there is no infinite loop.
   */

  window.fetch =
    async function(
      ...args
    ) {

      const response =
        await nativeFetch(
          ...args
        );


      try {

        if (
          response.ok &&
          isCartMutation(
            args[0]
          )
        ) {

          scheduleCartMutationSync();

        }

      }

      catch (error) {

        console.error(
          'Cart mutation watcher error:',
          error
        );

      }


      return response;

    };


  /*
   * ==========================================
   * INITIAL SYNCHRONIZATION
   * ==========================================
   */

  async function initialSync() {

    updateProductCalculations();


    try {

      const cart =
        await getCart();


      updateAllCalculations(
        cart
      );


      const deliveryType =
        cart.attributes?.[
          'Delivery Type'
        ] || '';


      /*
       * If customer previously selected Express,
       * make sure the fee quantity still matches
       * actual cart quantity.
       */

      if (deliveryType) {

        const changed =
          await ensureExpressFee({
            cart,
            deliveryType,
            showStatus:
              false
          });


        if (
          changed &&
          isCartPage()
        ) {

          window.location.reload();

          return;

        }


        if (changed) {

          const finalCart =
            await getCart();


          updateAllCalculations(
            finalCart
          );

        }

      }

    }

    catch (error) {

      console.error(
        'Delivery initialization error:',
        error
      );

    }

  }


  /*
   * ==========================================
   * INITIALIZE
   * ==========================================
   */

  function initialize() {

    openCartAfterReload();

    initialSync();

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );

  }

  else {

    initialize();

  }

})();